import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { ColumnState, GridApi } from 'ag-grid-community';
import { AG_GRID_CURRENT_USER_CONTEXT } from './ag-grid-current-user';
import { SetColumnSetting, SetPageSizeSetting } from './columns.action';
import { AgGridColumnSettingsState } from './columns.state';

@Injectable({ providedIn: 'root' })
export class AgGridSettingsService {
  private readonly store = inject(Store);
  private readonly currentUser = inject(AG_GRID_CURRENT_USER_CONTEXT);

  getInitialPageSize(gridId: string, defaultSize: number): number {
    const userId = this.currentUser.getCurrentUserId();
    if (userId) {
      const stored = this.store.selectSnapshot(
        AgGridColumnSettingsState.pageSize({ userId, gridId }),
      );
      if (stored !== undefined) return stored;
    }
    return defaultSize;
  }

  getColumnStates(gridId: string): ColumnState[] | undefined {
    const userId = this.currentUser.getCurrentUserId();
    if (!userId) return undefined;
    return this.store.selectSnapshot(
      AgGridColumnSettingsState.columnStates({ userId, gridId }),
    );
  }

  saveColumnState(gridId: string, gridApi: GridApi): void {
    const userId = this.currentUser.getCurrentUserId();
    if (userId) {
      this.store.dispatch(
        new SetColumnSetting({
          userId,
          gridId,
          columnStates: gridApi.getColumnState(),
        }),
      );
    }
  }

  restoreColumnState(
    gridId: string,
    gridApi: GridApi,
    defaultColumns: string[],
  ): void {
    const columnStates = this.getColumnStates(gridId);
    if (columnStates) {
      gridApi.applyColumnState({ state: columnStates, applyOrder: true });
      gridApi.refreshHeader();
    } else {
      gridApi.setColumnsVisible(gridApi.getColumns() || [], false);
      gridApi.setColumnsVisible(defaultColumns, true);
      gridApi.refreshHeader();
    }
  }

  savePageSize(gridId: string, pageSize: number, gridApi: GridApi): void {
    if (gridApi.getGridOption('paginationPageSize') !== pageSize) {
      gridApi.setGridOption('paginationPageSize', pageSize);
      gridApi.setGridOption('cacheBlockSize', pageSize);
    }

    const userId = this.currentUser.getCurrentUserId();
    if (userId) {
      this.store.dispatch(new SetPageSizeSetting({ userId, gridId, pageSize }));
    }
  }
}
